import { Avatar } from "@/components/catalyst/avatar";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { Subheading } from "@/components/catalyst/heading";
import { Text } from "@/components/catalyst/text";
import { Button } from "@/components/catalyst/button";
import { InviteOrganizationMemberDialog } from "./InviteOrganizationMemberDialog";
import { EllipsisVerticalIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
} from "@/components/catalyst/dropdown";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ConfirmDeleteDialog } from "@/lib/Components/Dialog/ConfirmDeleteDialog";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";
import { useUser } from "@/lib/Auth/useUser";
import { useGqlQuery, useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { getInitials } from "@/lib/Utils/getInitials";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { parse } from "graphql";

type MemberRecord = {
  id: string;
  user: {
    id: string;
    name: string | null;
    primary_email: string;
  } | null;
};

type OrganisationMembersData = {
  organisationMembers: {
    data: MemberRecord[];
    paginatorInfo: {
      hasMorePages: boolean;
      currentPage: number;
    };
  };
};
type OrganisationMembersVars = {
  first?: number;
  page?: number;
};

const organisationMembersQuery = parse(`
  query OrganisationMembers($first: Int, $page: Int) {
    organisationMembers(
      first: $first
      page: $page
    ) {
      data {
        id
        user {
          id
          name
          primary_email
        }
      }
      paginatorInfo {
        hasMorePages
        currentPage
      }
    }
  }
`) as unknown as TypedDocumentNode<
  OrganisationMembersData,
  OrganisationMembersVars
>;

type RemoveOrganisationMemberData = { removeOrganisationMember: boolean };
type RemoveOrganisationMemberVars = {
  organisation_id: string;
  user_id: string;
};

const removeOrganisationMemberMutation = parse(`
  mutation RemoveOrganisationMember($organisation_id: ID!, $user_id: ID!) {
    removeOrganisationMember(
      organisation_id: $organisation_id
      user_id: $user_id
    )
  }
`) as unknown as TypedDocumentNode<
  RemoveOrganisationMemberData,
  RemoveOrganisationMemberVars
>;

export function OrganizationMembersList() {
  const { activeOrganisationId } = useActiveOrganisation();
  const currentUser = useUser();
  const { isAdmin } = useTenant();
  const t = useTranslations("settings.team.members");
  const tRemove = useTranslations("settings.team.removeDialog");

  const { data, isLoading, refetch } = useGqlQuery(organisationMembersQuery, {
    first: 50,
    page: 1,
  });

  const { mutateAsync: removeMember } = useGqlMutation(
    removeOrganisationMemberMutation,
  );

  const currentUserEmail = currentUser?.email ?? "";
  const isImoovaMember = currentUserEmail.endsWith("@imoova.com");
  const canSeeImmoovaMembers = isAdmin || isImoovaMember;

  const { open: openInviteDialog } = useDialog(InviteOrganizationMemberDialog);
  const { open } = useDialog(ConfirmDeleteDialog);

  if (isLoading) {
    return (
      <div className="py-8 text-center">
        <Text>{t("loadingMembers")}</Text>
      </div>
    );
  }

  const members = data?.organisationMembers?.data ?? [];
  const filteredMembers = members.filter((member) => {
    const memberEmail = member.user?.primary_email ?? "";
    if (memberEmail.endsWith("@imoova.com")) {
      return canSeeImmoovaMembers;
    }
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Subheading>
          {t("title")} ({filteredMembers.length})
        </Subheading>
        <Button outline onClick={() => openInviteDialog({})}>
          {t("inviteMember")}
        </Button>
      </div>
      <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {filteredMembers.map((member) => {
          const user = member.user;
          if (!user) return null;

          const initials = getInitials(user.name ?? "");

          return (
            <div
              key={member.id}
              className="flex items-center justify-between py-4"
            >
              <div className="flex items-center gap-4">
                <Avatar
                  initials={initials}
                  alt={user.name ?? ""}
                  className="size-10"
                />
                <div className="font-medium text-zinc-950 dark:text-white">
                  <p>{user.name}</p>
                  <p className="text-xs text-gray-500">{user.primary_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-shrink">
                  <Dropdown>
                    <DropdownButton plain aria-label="Member actions">
                      <EllipsisVerticalIcon />
                    </DropdownButton>
                    <DropdownMenu>
                      <DropdownItem
                        onClick={() => {
                          open({
                            title: tRemove("title", {
                              name: user.name ?? "--",
                            }),
                            message: tRemove("message", {
                              name: user.name ?? "--",
                              organization: "",
                            }),
                            buttonText: tRemove("button"),
                            onDelete: async () => {
                              try {
                                await removeMember({
                                  organisation_id: activeOrganisationId!,
                                  user_id: user.id,
                                });
                                refetch();
                              } catch (err: any) {
                                toast.error(err?.message ?? tRemove("error"));
                              }
                            },
                          });
                        }}
                      >
                        <TrashIcon data-slot="icon" />
                        {t("removeMember")}
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
